import { PATTERN_CONTAINER } from './scale';
import { type PatternSx } from './types';

/**
 * Where each motif stops.
 *
 * **Nothing reaches an edge.** Every pattern is masked to fade out before the
 * band boundary. A motif that runs into the next section's background turns a
 * soft tonal change into a hard seam.
 *
 * The two full-band masks are stated in percentages and stay that way. A
 * `radial-gradient`'s percentage radii are resolved per axis — 90% of the
 * width and 80% of the height — so the ellipse is already proportional to
 * whatever box it is in, and covers the same middle 79% × 70% of a 390px phone
 * band as of a 2560px desktop one. There is nothing here for a container query
 * to fix; the thing that was wrong at both extremes was the motif's period,
 * and that is `scale.ts`.
 */

/** A soft oval: full strength through the middle, gone before every edge. */
export const OVAL_MASK =
  'radial-gradient(90% 80% at 50% 50%, #000 0%, rgba(0,0,0,0.55) 58%, transparent 88%)';

/**
 * A ring: clear in the middle, clear at the edges, present only in between.
 * For a band whose middle is a form — the fields keep a plain surface behind
 * them, and the texture still never meets the neighbouring band.
 */
export const RING_MASK =
  'radial-gradient(78% 78% at 50% 50%, transparent 18%, rgba(0,0,0,0.92) 62%, transparent 96%)';

/**
 * Where the beams converge, and the one piece of geometry in this set that
 * depends on the layout rather than on the band.
 *
 * ## What was wrong
 *
 * The lead form is the page's conversion point and the only band whose content
 * has a focal object rather than an even spread of cards, so this is the only
 * motif that is aimed. It used to be aimed at `72% 50%` of the band — and 72%
 * is the right number measured against the wrong box. The form sits in a
 * `Container maxWidth="lg"`, so it is 72% of the *container's content width*,
 * which is 1200px minus its 24px gutters however wide the band gets. The band
 * is full-bleed. The two agree only when the viewport is about 1200px wide,
 * and diverge by half the gutter either side of that: at 1440 the rays landed
 * 63px right of the form, and at 2560 they landed 310px right of it, in the
 * empty margin outside the container, taking the red bloom with them.
 *
 * ## What it is now
 *
 * The same 72%, measured against the box it was always a fraction of, and
 * reconstructed in CSS from the container's own rules — `min(100cqi, 1200px)`
 * for its outer box and 24px for the gutter MUI's `MuiContainer` override
 * sets. `100cqi` is the band, because the layer that establishes the container
 * is inset into it.
 *
 * The aim is therefore correct at every width rather than at one, and this is
 * the single place where the desktop baseline deliberately moves: 1440 now
 * aims 63px further left than it did, because that is where the form is.
 * To restore the old rendering exactly, set `--kabex-beam-origin` back to
 * `72% 50%`.
 *
 * Below the step the form is under the copy and full width, so the origin comes
 * back to the middle. Stated as a container query rather than a `md`
 * breakpoint so the motif keeps behaving if it is ever painted into something
 * narrower than a band.
 */
export const BEAM_ORIGIN_SX: PatternSx = {
  '--kabex-beam-origin': '50% 38%',
  [`@container ${PATTERN_CONTAINER} (min-width: 900px)`]: {
    /** The Container's outer box: capped at `lg`, the band's width below that. */
    '--kabex-beam-box': 'min(100cqi, 1200px)',
    /** Its content's left edge, in band coordinates. */
    '--kabex-beam-inset': 'calc((100cqi - var(--kabex-beam-box)) / 2 + 24px)',
    /** Its content width — the box the 72% is a fraction of. */
    '--kabex-beam-span': 'calc(var(--kabex-beam-box) - 48px)',
    '--kabex-beam-origin': 'calc(var(--kabex-beam-inset) + var(--kabex-beam-span) * 0.72) 50%',
  },
};

/**
 * Clear at the origin, present in a ring around it, gone before the edges.
 *
 * Both halves matter. The outer fade is the usual rule that no motif reaches a
 * band boundary; the inner one is what keeps the fields on a plain surface,
 * and it also disposes of the part of a conic gradient that cannot be drawn —
 * near the point of convergence the rays are narrower than a pixel and turn
 * into moire.
 */
export const BEAM_MASK =
  'radial-gradient(54% 92% at var(--kabex-beam-origin), transparent 20%, rgba(0,0,0,0.95) 46%, transparent 92%)';
